/**
 * WeightTotalsWarning — animated banner shown while weight totals are off 100%.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Uses the `grid-template-rows: 0fr / 1fr` + opacity trick (same as the group
 * card body) so it smoothly expands in / collapses out as the user types,
 * rather than popping. Content stays mounted so the transition can play.
 */

import { FC } from 'react';
import { AlertTriangle } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { WeightTotalIssue } from './weight-validation';

export interface WeightTotalsWarningProps {
  issues: WeightTotalIssue[];
  className?: string;
}

function fixHint(total: number): string {
  return total > 100 ? `remove ${total - 100}%` : `add ${100 - total}%`;
}

const WeightTotalsWarning: FC<WeightTotalsWarningProps> = ({
  issues,
  className,
}) => {
  const open = issues.length > 0;
  return (
    <div
      aria-hidden={!open}
      className={cn(
        'grid transition-[grid-template-rows,opacity] duration-200 ease-out',
        open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        className,
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-2.5 text-warning"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div className="min-w-0 flex-1 space-y-0.5 text-xs">
            <p className="font-semibold">
              Weights must total 100% before saving
            </p>
            <ul className="space-y-0.5">
              {issues.map((issue) => (
                <li key={issue.label}>
                  <span className="font-medium">{issue.label}</span> total{' '}
                  <span className="tabular-nums">{issue.total}%</span> —{' '}
                  {fixHint(issue.total)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeightTotalsWarning;
